import { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import {
  CircleDollarSign,
  Users,
  CheckCheck,
  Clock,
  ShieldAlert,
  TrendingUp,
} from "lucide-react";
import { api, peso } from "../../lib/api";
import { BrutalCard, Badge, SectionTitle } from "../../components/common/Brutal";

const PIE_COLORS = ["#E5247C", "#FFB400", "#3D1E6D", "#10B981", "#EF4444"];

export default function AdminOperations() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data));
  }, []);

  if (!stats) return <div>Loading…</div>;

  const cards = [
    {
      label: "Revenue (Paid)",
      value: peso(stats.revenue),
      icon: CircleDollarSign,
      color: "bg-[#E5247C] text-white",
    },
    {
      label: "Pending Revenue",
      value: peso(stats.pending_revenue),
      icon: TrendingUp,
      color: "bg-[#FFB400] text-[#3D1E6D]",
    },
    {
      label: "Total Campers",
      value: stats.total_campers,
      icon: Users,
      color: "bg-white text-[#3D1E6D]",
    },
    {
      label: "Bookings",
      value: stats.total_bookings,
      icon: Users,
      color: "bg-white text-[#3D1E6D]",
    },
    {
      label: "Checked In",
      value: stats.checked_in,
      icon: CheckCheck,
      color: "bg-[#10B981] text-white",
    },
    {
      label: "Pending Arrivals",
      value: stats.pending_arrivals,
      icon: Clock,
      color: "bg-white text-[#3D1E6D]",
    },
    {
      label: "Checked Out",
      value: stats.checked_out,
      icon: CheckCheck,
      color: "bg-[#5B2B82] text-white",
    },
    {
      label: "Overdue",
      value: stats.overdue,
      icon: ShieldAlert,
      color: "bg-[#EF4444] text-white",
    },
  ];

  return (
    <div className="space-y-6" data-testid="admin-operations">
      <SectionTitle kicker="Command Center" title="Festival Operations" />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cards.map((c) => (
          <BrutalCard key={c.label} className="p-4">
            <div
              className={`h-9 w-9 grid place-items-center kf-border ${c.color}`}
            >
              <c.icon className="h-4 w-4" strokeWidth={2.75} />
            </div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#6B4E9E] mt-2">
              {c.label}
            </div>
            <div className="display text-3xl mt-1">{c.value}</div>
          </BrutalCard>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <BrutalCard className="p-5">
          <h3 className="display text-2xl mb-3">Zone Occupancy</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={stats.zone_occupancy}>
              <CartesianGrid strokeDasharray="3 3" stroke="#3D1E6D20" />
              <XAxis dataKey="zone" tick={{ fontSize: 11, fill: "#3D1E6D" }} />
              <YAxis tick={{ fontSize: 11, fill: "#3D1E6D" }} />
              <Tooltip
                contentStyle={{
                  border: "2px solid #3D1E6D",
                  background: "#fff",
                  borderRadius: 0,
                  boxShadow: "4px 4px 0 #3D1E6D",
                  fontWeight: 700,
                }}
              />
              <Bar dataKey="occupied" fill="#E5247C" stroke="#3D1E6D" strokeWidth={1} />
              <Bar dataKey="available" fill="#FFB400" stroke="#3D1E6D" strokeWidth={1} />
            </BarChart>
          </ResponsiveContainer>
        </BrutalCard>

        <BrutalCard className="p-5">
          <h3 className="display text-2xl mb-3">Camper Status</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={stats.status_breakdown}
                dataKey="value"
                nameKey="name"
                outerRadius={100}
                stroke="#3D1E6D"
                strokeWidth={2}
                label={({ name, value }) => `${name}: ${value}`}
              >
                {stats.status_breakdown.map((_, i) => (
                  <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Legend />
              <Tooltip
                contentStyle={{
                  border: "2px solid #3D1E6D",
                  background: "#fff",
                  borderRadius: 0,
                  boxShadow: "4px 4px 0 #3D1E6D",
                  fontWeight: 700,
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </BrutalCard>

        <BrutalCard className="p-5 lg:col-span-2">
          <h3 className="display text-2xl mb-3">Revenue by Camping Type</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={stats.revenue_by_type} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#3D1E6D20" />
              <XAxis type="number" tick={{ fontSize: 11, fill: "#3D1E6D" }} tickFormatter={(v) => `₱${v}`} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: "#3D1E6D" }} />
              <Tooltip
                contentStyle={{
                  border: "2px solid #3D1E6D",
                  background: "#fff",
                  borderRadius: 0,
                  boxShadow: "4px 4px 0 #3D1E6D",
                  fontWeight: 700,
                }}
                formatter={(v) => peso(v)}
              />
              <Bar dataKey="value" fill="#E5247C" stroke="#3D1E6D" strokeWidth={1} />
            </BarChart>
          </ResponsiveContainer>
        </BrutalCard>
      </div>

      <BrutalCard className="p-5">
        <h3 className="display text-2xl mb-3">Zone Capacity Detail</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {stats.zone_occupancy.map((z) => {
            const pct = Math.round((z.occupied / z.capacity) * 100);
            return (
              <div key={z.zone} className="kf-border bg-[#FFF4E6] p-4">
                <div className="flex items-center justify-between">
                  <div className="font-bold display text-xl">{z.zone}</div>
                  <Badge color={pct >= 90 ? "red" : pct >= 60 ? "amber" : "green"}>
                    {pct}%
                  </Badge>
                </div>
                <div className="mt-2 h-3 kf-border bg-white">
                  <div
                    className="h-full bg-[#E5247C]"
                    style={{ width: `${Math.min(100, pct)}%` }}
                  />
                </div>
                <div className="text-xs text-[#6B4E9E] mt-1">
                  {z.occupied} / {z.capacity} slots
                </div>
              </div>
            );
          })}
        </div>
      </BrutalCard>
    </div>
  );
}
